import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  AIStudioDisplayMode,
  AIStudioOpenContext,
  AIStudioOpenOptions,
} from './AIStudioContext';
import { AIStudioContext } from './AIStudioContext';
import useAIStudioTracking from 'src/components/global-ai-studio/hooks/useAIStudioTracking';
import { useToast } from 'src/components/global-toast/ToastContext';
import { useIsFeatureFlagEnabled } from 'src/hooks/hooks';
import {
  type AIStudioConversationReadyEvent,
  type AIStudioConversationStartedEvent,
  useListenAIStudioConversationEvents,
} from 'src/hooks/PusherEventHooks';
import {
  aiStudioApi,
  useLazyGetAIStudioConversationsQuery,
} from 'src/services/ai-studio/aiStudioApi';
import { useGetPermissionsQuery } from 'src/services/dashboard-api';
import { useAppDispatch } from 'src/store/hooks';
import { getUserRole, Permission, UserRoles } from 'src/utils/enums';

const importModal = () => import('./AIStudioModal');
const AIStudioModal = lazy(importModal);

const DISPLAY_MODE_STORAGE_KEY = 'ai-studio-display-mode';
const DISPLAY_MODES: AIStudioDisplayMode[] = ['fullscreen', 'sidePanel'];

// The gates for the side-panel display mode: the ai_studio_sidebar_ux
// feature flag AND the SUPER_ADMIN role. Both on → the header's mode
// toggle is shown and usable on every page; otherwise hidden, and a
// stored side-panel preference is suppressed (not overwritten), so it
// returns when the gates turn on.
const AI_STUDIO_SIDEBAR_UX_FLAG = 'ai_studio_sidebar_ux';

const isSuperAdmin = () => getUserRole() === UserRoles.ROLE_SUPER_ADMIN;

// Last-used mode survives reloads; storage errors (private mode) fall
// back to the default fullscreen mode.
const readStoredDisplayMode = (): AIStudioDisplayMode => {
  try {
    const stored = localStorage.getItem(DISPLAY_MODE_STORAGE_KEY);
    return DISPLAY_MODES.includes(stored as AIStudioDisplayMode)
      ? (stored as AIStudioDisplayMode)
      : 'fullscreen';
  } catch {
    return 'fullscreen';
  }
};

/**
 * Lightweight context provider for AI Studio.
 *
 * Wraps children with a context so any descendant can call `open()`.
 * The heavy modal content (CopilotKit, charts, sidebar) is lazy-loaded
 * but the chunk is preloaded on idle so opening feels instant.
 */
const AIStudioProvider = ({ children }: { children?: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [displayMode, setDisplayModeState] = useState<AIStudioDisplayMode>(
    readStoredDisplayMode,
  );
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  // Bundled modal-consumed-at-open fields. `pendingPrompt` stays separate
  // because the chat (not the modal) consumes it after the first message.
  const [openContext, setOpenContext] = useState<AIStudioOpenContext | null>(
    null,
  );

  const { data: permissions = {} } = useGetPermissionsQuery();
  const isAIStudioEnabled = Boolean(permissions[Permission.AI_STUDIO]);

  const [isLoading, setIsLoading] = useState(false);

  // Preload the modal chunk once the browser is idle so it's cached before click
  useEffect(() => {
    if (!isAIStudioEnabled) {
      return;
    }
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => importModal());
      return () => window.cancelIdleCallback(id);
    }
    // Fallback for Safari: preload after a short delay
    const timer = window.setTimeout(() => importModal(), 2000);
    return () => window.clearTimeout(timer);
  }, [isAIStudioEnabled]);

  const open = useCallback(
    ({ entryMethod, prompt, threadId }: AIStudioOpenOptions = {}) => {
      if (prompt) {
        setPendingPrompt(prompt);
      }
      if (entryMethod || threadId) {
        setOpenContext({ entryMethod, threadId });
      }
      setIsOpen(true);
      if (!hasOpened) {
        setIsLoading(true);
      }
      setHasOpened(true);
    },
    [hasOpened],
  );

  const close = useCallback(() => setIsOpen(false), []);

  const trackEvent = useAIStudioTracking();

  const setDisplayMode = useCallback(
    (mode: AIStudioDisplayMode) => {
      setDisplayModeState(mode);
      try {
        localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, mode);
      } catch {
        // Private mode / storage full — persistence is best-effort.
      }
      trackEvent('studio_display_mode_changed', { mode });
    },
    [trackEvent],
  );

  // The page integration (content push + geometry vars) is owned by the app
  // Layout (src/components/app/layout), which reads this context. Gate:
  // the stored preference is suppressed unless both gates are on, but not
  // overwritten.
  const isSidePanelAvailable =
    useIsFeatureFlagEnabled(AI_STUDIO_SIDEBAR_UX_FLAG) && isSuperAdmin();
  const effectiveDisplayMode: AIStudioDisplayMode = isSidePanelAvailable
    ? displayMode
    : 'fullscreen';

  const handlePendingPromptConsumed = useCallback(
    () => setPendingPrompt(null),
    [],
  );

  const handleOpenContextConsumed = useCallback(() => setOpenContext(null), []);

  const handleModalReady = useCallback(() => setIsLoading(false), []);

  // ── Toast on RQ completion ─────────────────────────────────────────
  const { showToast } = useToast();
  const dispatch = useAppDispatch();
  const [fetchConversations] = useLazyGetAIStudioConversationsQuery();

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const activeConversationIdRef = useRef<string | null>(null);

  const handleActiveConversationChange = useCallback(
    (conversationId: string | null) => {
      activeConversationIdRef.current = conversationId;
    },
    [],
  );

  // Fetch the entire conversations list (bypassing pagination) so older
  // threads that just streamed a response also surface, then mirror the
  // result into the no-arg cache slot used by useConversations as its
  // first page — this collapses any previously paginated state into a
  // single complete list with has_more=false.
  const refreshAllConversations = useCallback(async () => {
    const { data } = await fetchConversations({ all: true });
    if (data) {
      dispatch(
        aiStudioApi.util.upsertQueryData(
          'getAIStudioConversations',
          undefined,
          data,
        ),
      );
    }
    return data;
  }, [dispatch, fetchConversations]);

  const handleConversationReady = useCallback(
    async ({ thread_id }: AIStudioConversationReadyEvent) => {
      const data = await refreshAllConversations();

      // Suppress the toast only when the modal is open AND the user is on
      // that exact conversation — they already see it streaming in place.
      if (isOpenRef.current && activeConversationIdRef.current === thread_id) {
        return;
      }

      const conv = data?.conversations?.find(
        c => c.conversation_id === thread_id,
      );
      const preview = conv?.title ?? 'New conversation is ready.';

      showToast({
        id: `ai-studio-conversation-ready-${thread_id}`,
        onClick: () =>
          open({ entryMethod: 'toast_click', threadId: thread_id }),
        preview,
      });
    },
    [refreshAllConversations, showToast, open],
  );

  const handleConversationStarted = useCallback(
    (_event: AIStudioConversationStartedEvent) => {
      // Refresh so the sidebar's in-progress indicator appears for the
      // run that just began streaming, and so a resumed older thread
      // re-surfaces in the list.
      void refreshAllConversations();
    },
    [refreshAllConversations],
  );

  useListenAIStudioConversationEvents({
    onReady: handleConversationReady,
    onStarted: handleConversationStarted,
  });

  useEffect(() => {
    if (!isAIStudioEnabled) {
      return;
    }

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;

      if (e.key === '/' && !isInput && !isOpen) {
        e.preventDefault();
        open({ entryMethod: 'slash_key' });
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isAIStudioEnabled, isOpen, open]);

  const contextValue = useMemo(
    () => ({
      displayMode: effectiveDisplayMode,
      isLoading,
      isOpen,
      isSidePanelAvailable,
      open,
      setDisplayMode,
    }),
    [
      effectiveDisplayMode,
      isLoading,
      isOpen,
      isSidePanelAvailable,
      open,
      setDisplayMode,
    ],
  );

  return (
    <AIStudioContext.Provider value={contextValue}>
      {children}
      {hasOpened && (
        <Suspense fallback={null}>
          <AIStudioModal
            isOpen={isOpen}
            onActiveConversationChange={handleActiveConversationChange}
            onClose={close}
            onOpenContextConsumed={handleOpenContextConsumed}
            onPendingPromptConsumed={handlePendingPromptConsumed}
            onReady={handleModalReady}
            openContext={openContext}
            pendingPrompt={pendingPrompt}
          />
        </Suspense>
      )}
    </AIStudioContext.Provider>
  );
};

export default AIStudioProvider;
